import { useState } from 'react';
import { TextField, MenuItem } from '@mui/material';
import { Acc } from '../../../../components/edit-object/design/Accordion';
import { Btn, FLabel, Input } from '../../../../components/edit-object/design/Form';
import type { Measurement } from '../../../../components/MeasurementField';
import {
  DIMENSION_TYPES,
  getDefaultUnitForDimension,
  getUnitsForDimension,
} from '../../../../constants/measurementDimensions';
import {
  getFractionOptions,
  unitSupportsFractions,
} from '../../../../constants/measurementFractions';
import {
  convertDimension,
  parseValueWithFraction,
} from '../../../../utils/dimensionConversion';
import type { ArtworkDetailsForm } from '../useArtworkDetailsForm';

interface Props {
  form: ArtworkDetailsForm;
}

const DUAL_TARGET_UNIT: Record<string, string> = {
  inches: 'cm',
  feet: 'cm',
  cm: 'inches',
  mm: 'inches',
  meters: 'inches',
};

const roundConverted = (value: number, unit: string): string => {
  const decimals = unit === 'cm' || unit === 'mm' || unit === 'meters' ? 1 : 2;
  return value.toFixed(decimals).replace(/\.?0+$/, '');
};

const formatRowSummary = (d: Measurement, fallbackUnit?: string): string => {
  const unit = d.unit ?? fallbackUnit ?? '';
  const valuePart = d.fraction ? `${d.value} ${d.fraction}`.trim() : d.value;
  return `${valuePart} ${unit}`.trim();
};

/**
 * Build a dual-unit summary string for the accordion header. When all
 * non-empty dimensions share a convertible unit, render
 * `v1 × v2 [× v3] unit (c1 × c2 [× c3] toUnit)`. Otherwise fall back to
 * per-row joined formatting.
 */
const buildDualSummary = (
  dimensions: Measurement[],
  fallbackUnit: string,
): string => {
  const filled = dimensions.filter((d) => d.value);
  if (filled.length === 0) return '';

  const firstUnit = filled[0].unit ?? fallbackUnit;
  const target = DUAL_TARGET_UNIT[firstUnit];
  const allSame = filled.every((d) => (d.unit ?? fallbackUnit) === firstUnit);

  if (!target || !allSame) {
    return filled.map((d) => formatRowSummary(d, fallbackUnit)).join(' × ');
  }

  const primary = filled.map((d) =>
    d.fraction ? `${d.value} ${d.fraction}`.trim() : d.value,
  );
  const converted: (string | null)[] = filled.map((d) => {
    const n = parseValueWithFraction(d.value, d.fraction ?? null);
    if (!Number.isFinite(n)) return null;
    const c = convertDimension(n, firstUnit, target);
    return Number.isFinite(c) ? roundConverted(c, target) : null;
  });

  if (converted.some((v) => v === null)) {
    return filled.map((d) => formatRowSummary(d, fallbackUnit)).join(' × ');
  }

  return `${primary.join(' × ')} ${firstUnit} (${converted.join(' × ')} ${target})`;
};

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z" />
  </svg>
);

interface DimensionRowsProps {
  /** Used for ID prefix to keep label/control associations unique across the two row groups. */
  idPrefix: string;
  dimensions: Measurement[];
  setDimensions: (next: Measurement[]) => void;
  unit: string;
}

const REQUIRED_TYPES = new Set(['Height', 'Width']);

const DimensionRows = ({ idPrefix, dimensions, setDimensions, unit }: DimensionRowsProps) => {
  const [pendingType, setPendingType] = useState('');

  const update = (i: number, patch: Partial<Measurement>) => {
    setDimensions(
      dimensions.map((d, idx) => (idx !== i ? d : { ...d, ...patch })),
    );
  };

  const remove = (i: number) =>
    setDimensions(dimensions.filter((_, idx) => idx !== i));

  const usedTypes = new Set(dimensions.map((d) => d.type));
  const availableTypes = DIMENSION_TYPES.filter((t) => !usedTypes.has(t));

  const addPending = () => {
    if (!pendingType || usedTypes.has(pendingType)) return;
    setDimensions([
      ...dimensions,
      {
        type: pendingType,
        value: '',
        fraction: '',
        unit: getDefaultUnitForDimension(pendingType),
      },
    ]);
    setPendingType('');
  };

  return (
    <>
      {dimensions.map((d, i) => {
        const rowUnit = d.unit ?? unit;
        const unitOptions = getUnitsForDimension(d.type);
        const showFraction = unitSupportsFractions(rowUnit);
        const fractionOptions = getFractionOptions(rowUnit);
        const isRequired = REQUIRED_TYPES.has(d.type);
        return (
          <div className="meas-row" key={i}>
            <div>
              {i === 0 && <FLabel>Dimension</FLabel>}
              <div
                id={`${idPrefix}-type-${i}`}
                className="meas-fixed-type"
              >
                {d.type}
              </div>
            </div>
            <div>
              {i === 0 && <FLabel htmlFor={`${idPrefix}-value-${i}`}>Value</FLabel>}
              <Input
                id={`${idPrefix}-value-${i}`}
                value={d.value}
                onChange={(e) => update(i, { value: e.target.value })}
                placeholder="0"
                inputMode="decimal"
              />
            </div>
            <div>
              {i === 0 && (
                <FLabel htmlFor={`${idPrefix}-fraction-${i}`}>Fraction</FLabel>
              )}
              {showFraction ? (
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={d.fraction || ''}
                  onChange={(e) => update(i, { fraction: e.target.value })}
                  slotProps={{ htmlInput: { id: `${idPrefix}-fraction-${i}` } }}
                >
                  <MenuItem value="">—</MenuItem>
                  {fractionOptions.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <div aria-hidden="true" />
              )}
            </div>
            <div>
              {i === 0 && <FLabel htmlFor={`${idPrefix}-unit-${i}`}>Unit</FLabel>}
              <TextField
                select
                size="small"
                fullWidth
                value={rowUnit}
                onChange={(e) => update(i, { unit: e.target.value })}
                slotProps={{ htmlInput: { id: `${idPrefix}-unit-${i}` } }}
              >
                {unitOptions.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div>
              {!isRequired && (
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => remove(i)}
                  title="Remove dimension"
                  aria-label={`Remove dimension ${i + 1}`}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {availableTypes.length > 0 && (
        <div className="meas-add-row">
          <TextField
            select
            size="small"
            value={pendingType}
            onChange={(e) => setPendingType(e.target.value)}
            placeholder="Select dimension"
            sx={{ width: 180 }}
            slotProps={{ inputLabel: { shrink: false } }}
            aria-label="Dimension type to add"
          >
            <MenuItem value="" disabled>
              Select dimension
            </MenuItem>
            {availableTypes.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <Btn
            variant="outlined"
            onClick={addPending}
            disabled={!pendingType}
          >
            <PlusIcon />
            Add
          </Btn>
        </div>
      )}
    </>
  );
};

export const MeasurementsSection = ({ form }: Props) => {
  const {
    dimensions,
    setDimensions,
    unit,
    hasDifferentSubstrate,
    setHasDifferentSubstrate,
    substrateUnit,
    substrateDimensions,
    setSubstrateDimensions,
  } = form;

  const summary = buildDualSummary(dimensions, unit);
  const substrateSummary = buildDualSummary(substrateDimensions, substrateUnit);

  return (
    <>
      <Acc
        id="sec-measurements"
        title="Measurements"
        summary={summary}
        emptyPlaceholder="No dimensions"
        defaultExpanded
      >
        <DimensionRows
          idPrefix="dim"
          dimensions={dimensions}
          setDimensions={setDimensions}
          unit={unit}
        />

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 16,
            cursor: 'pointer',
            color: 'var(--slate-700)',
            fontSize: 14,
          }}
        >
          <input
            type="checkbox"
            checked={hasDifferentSubstrate}
            onChange={(e) => setHasDifferentSubstrate(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            If your substrate size differs from your object size (e.g. paper
            size is larger than the print area), select this checkbox to note
            it here
          </span>
        </label>
      </Acc>

      {hasDifferentSubstrate && (
        <Acc
          id="sec-substrate-measurements"
          title="Substrate Measurements"
          summary={substrateSummary}
          emptyPlaceholder="No substrate dimensions"
          defaultExpanded
        >
          <DimensionRows
            idPrefix="sub-dim"
            dimensions={substrateDimensions}
            setDimensions={setSubstrateDimensions}
            unit={substrateUnit}
          />
        </Acc>
      )}
    </>
  );
};
